import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowUpRight, DollarSign, Target, Check, ChevronRight, BarChart3, TrendingUp } from 'lucide-react';

const insights = [
  {
    type: 'Preço Dinâmico',
    title: 'Otimização de Margem GNV',
    impact: '+ R$ 0,12 / m³',
    desc: 'O LukIA detectou uma redução na concorrência em um raio de 2km. Sugerimos ajuste de preço para maximizar margem.',
    date: 'Hoje, 09:45'
  },
  {
    type: 'Eficiência',
    title: 'Redução de Perda Operacional',
    impact: '- 4.2% Perdas',
    desc: 'Integração de medição eletrônica detectou divergência de 15L no tanque 2. Manutenção recomendada.',
    date: 'Ontem, 18:20'
  }
];

export default function IAMarginInsights() {
  return (
    <section className="py-24 lg:py-40 bg-white relative overflow-hidden font-sans">
      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
           
           {/* Left: Content */}
           <div className="lg:col-span-5 flex flex-col items-start">
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 text-amber-600 font-black text-[10px] uppercase tracking-widest border border-amber-100 mb-8"
              >
                 <Sparkles size={14} /> Lucratividade Inteligente
              </motion.div>

              <h2 className="text-4xl lg:text-5xl font-black text-slate-900 leading-[1.1] tracking-tight mb-8">
                Maximize cada centavo da sua <span className="text-amber-500">margem.</span>
              </h2>
              
              <p className="text-slate-500 text-lg font-medium leading-relaxed mb-12">
                O LukIA analisa o mercado, seus custos de aquisição e o comportamento do cliente para sugerir as melhores estratégias de preço e evitar perdas invisíveis.
              </p>

              <div className="space-y-6 w-full">
                 {[
                   { label: 'Aumento Médio de Lucro', value: '18%', icon: <TrendingUp size={20}/> },
                   { label: 'ROI Garantido', value: '3.2x', icon: <ArrowUpRight size={20}/> }
                 ].map((item, i) => (
                   <motion.div 
                     key={i}
                     initial={{ opacity: 0, x: -20 }}
                     whileInView={{ opacity: 1, x: 0 }}
                     transition={{ delay: 0.2 + (i * 0.1) }}
                     className="flex items-center gap-6 p-6 rounded-[2rem] bg-slate-50 border border-slate-100"
                   >
                      <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-amber-500">
                         {item.icon}
                      </div>
                      <div>
                         <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">{item.label}</p>
                         <p className="text-2xl font-black text-slate-900">{item.value}</p>
                      </div>
                   </motion.div>
                 ))}
              </div>
           </div>

           {/* Right: Insight Interactive Cards */}
           <div className="lg:col-span-7 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-amber-50 blur-[120px] rounded-full -z-10 opacity-60" />
              
              <div className="space-y-8 relative">
                 {insights.map((insight, idx) => (
                   <motion.div
                     key={idx}
                     initial={{ opacity: 0, scale: 0.95, x: 20 }}
                     whileInView={{ opacity: 1, scale: 1, x: 0 }}
                     viewport={{ once: true }}
                     transition={{ delay: 0.3 + (idx * 0.2), duration: 0.8 }}
                     className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] border border-white hover:shadow-2xl transition-all duration-500"
                   >
                      <div className="flex items-center justify-between mb-8">
                         <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white">
                               <Target size={18}/>
                            </div>
                            <div>
                               <p className="text-[9px] font-black tracking-[0.2em] text-amber-500 uppercase">{insight.type}</p>
                               <h3 className="text-lg font-black text-slate-900 tracking-tight">{insight.title}</h3>
                            </div>
                         </div>
                         <div className="text-right">
                            <p className="text-[10px] font-bold text-slate-400 uppercase">{insight.date}</p>
                         </div>
                      </div>

                      <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                         <div className="flex-1">
                            <p className="text-slate-500 text-sm font-medium leading-relaxed">
                               {insight.desc}
                            </p>
                         </div>
                         <div className="px-6 py-4 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex flex-col items-center">
                            <span className="text-[9px] font-black uppercase tracking-widest mb-1">Impacto Lucro</span>
                            <span className="text-xl font-black whitespace-nowrap">{insight.impact}</span>
                         </div>
                      </div>

                      <div className="flex items-center gap-4">
                         <button className="flex-1 py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-slate-900/20 transition-all flex items-center justify-center gap-2">
                            Aprovar Ajuste <Check size={14}/>
                         </button>
                         <button className="px-8 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all">
                            Ignorar
                         </button>
                      </div>
                   </motion.div>
                 ))}

                 {/* Decorative stats bar */}
                 <div className="bg-slate-50 border border-slate-100 p-6 rounded-[2rem] flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-4">
                       <BarChart3 size={18} className="text-slate-400" />
                       <span className="text-xs font-bold text-slate-500">Histórico de Performance IA</span>
                    </div>
                    <div className="flex gap-2">
                       {[60, 40, 80, 50, 90, 70].map((h, i) => (
                         <div key={i} className="w-2 rounded-full bg-amber-200" style={{ height: `${h}%` }} />
                       ))}
                    </div>
                 </div>
              </div>
           </div>

        </div>
      </div>
    </section>
  );
}
